/**
 * Arah sumbu kedalaman sebuah lembar.
 *
 * Empat acuan menghitung ke bawah — MD, TVDRT, TVD, TVDSS — dan satu
 * menghitung ke atas: ELEV, ketinggian di atas muka laut, yaitu TVDSS
 * dikalikan −1. Selisih
 * tandanya bukan soal selera. Teapot Dome duduk 1.500–1.684 m di atas laut dan
 * lubangnya lebih dangkal daripada itu, jadi pada TVDSS seluruh lembarnya
 * bertanda minus — −1.593 di kepala, −133 di kaki, tiap penanda "SHNNu
 * −1487,9" — dan yang membacanya melihat impor yang rusak alih-alih lapangan
 * yang tinggi.
 *
 * Yang berubah bukan hanya tandanya. Pada ELEV angka **terbesar** ada di
 * kepala lembar, jadi jendela lembarnya tidak boleh lagi berupa
 * [terkecil, terbesar]; kalau tetap begitu, sumurnya tergambar terbalik.
 * Karena itu semua di modul ini bekerja pada pasangan [atas, bawah] apa
 * adanya, dan tidak satu pun mengandaikan `base > top`.
 */
import type { DepthReference } from "../types";

/** Nama tiap acuan di kepala lembar. Sama dengan `DEPTH_LABEL` di backend. */
export const DEPTH_LABEL: Record<DepthReference, string> = {
  md: "MD",
  tvdrt: "TVDRT",
  tvd: "TVD",
  tvdss: "TVDSS",
  elev: "ELEV",
};

/**
 * Urutan di pemilih: yang diukur lebih dulu, lalu turun datum demi datum.
 *
 * MD diukur dari meja putar, jadi TVDRT adalah pasangan tegaknya langsung;
 * TVD memindahkan datum itu turun ke permukaan tanah; TVDSS turun lagi ke muka
 * laut; ELEV membalik arahnya.
 */
export const DEPTH_ORDER: DepthReference[] = ["md", "tvdrt", "tvd", "tvdss", "elev"];

/** Keterangan satu baris tiap acuan, untuk pemilih di kepala lembar. */
export const DEPTH_HINT: Record<DepthReference, string> = {
  md: "Measured along the hole",
  tvdrt: "Vertical from the rotary table",
  tvd: "Vertical from ground level",
  tvdss: "Vertical from sea level, positive downwards",
  elev: "Height above sea level (TVDSS × −1)",
};

/**
 * Kalimat penuh tiap acuan, untuk panel yang punya ruang menuliskannya.
 *
 * Sebuah `Record` dan bukan rantai operator bersyarat, sebab rantai itu boleh
 * kependekan tanpa satu pun keluhan: lembar korelasi menuliskannya sebagai
 * empat cabang untuk lima acuan, dan TVDRT — yang tidak disebut satu pun
 * cabangnya — jatuh ke cabang terakhir lalu mencetak keterangan ELEV. Yang
 * terbaca orang karena itu adalah "elevation above sea level, positive
 * upwards" pada sumbu yang justru menghitung ke bawah.
 *
 * Ditulis di sini bersama `DEPTH_HINT` supaya keduanya wajib lengkap: sebuah
 * acuan keenam tidak bisa ditambahkan tanpa `tsc` menuntut kalimatnya.
 *
 * TVDRT dan TVD dibedakan sampai ke datumnya, sebagaimana migrasi 0008
 * memisahkan keduanya: menyebut TVD "dari meja putar" menggeser seluruh
 * angkanya setinggi menara, dan itu persis kekeliruan yang dipisahkan migrasi
 * itu untuk diakhiri.
 */
export const DEPTH_CAPTION: Record<DepthReference, string> = {
  md: "Measured depth along the borehole — what the log file carries.",
  tvdrt:
    "True vertical depth from the rotary table — the direct vertical partner "
    + "of MD. Shorter than MD in a deviated well.",
  tvd:
    "True vertical depth from ground level: TVDRT less the height of the "
    + "rotary table above the ground. A well with no ground elevation recorded "
    + "falls back to the rotary table, and the sheet says so.",
  tvdss:
    "True vertical depth below sea level. The only reference comparable "
    + "between wells, because every rotary table sits at a different height.",
  elev:
    "TVDSS multiplied by −1: elevation above sea level, positive upwards. "
    + "Just as comparable between wells, and readable as height in an onshore "
    + "field whose holes end above sea level.",
};

/** Acuan yang angkanya mengecil ke bawah lubang. */
export function runsUpward(mode: string): boolean {
  return mode === "elev";
}

/**
 * Jendela lembar dari dua ujung, diurutkan menurut arah acuannya.
 *
 * Mengembalikan [atas, bawah] sebagaimana keduanya jatuh di layar — pada ELEV
 * itu berarti [terbesar, terkecil].
 */
export function orientRange(a: number, b: number, mode: string): [number, number] {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return runsUpward(mode) ? [hi, lo] : [lo, hi];
}

/**
 * Kedudukan tegak sebuah kedalaman pada lembar, 0 di kepala dan 1 di kaki.
 *
 * `null` berarti di luar lembar. Perhatikan bahwa yang diuji adalah pecahannya,
 * bukan `depth < top || depth > base`: uji yang kedua benar hanya selama
 * sumbunya menaik, dan diam-diam membuang seluruh kurva ketika tidak.
 */
export function axisFraction(depth: number, top: number, base: number): number | null {
  const span = base - top;
  if (!Number.isFinite(span) || span === 0 || !Number.isFinite(depth)) return null;
  const t = (depth - top) / span;
  if (t < 0 || t > 1) return null;
  // `+ 0` membuang −0, yang muncul di kepala setiap lembar yang menurun dan
  // menetes sampai ke jalur SVG sebagai "-0.0".
  return t + 0;
}

/** Panjang jendela dalam meter — selalu positif, ke arah mana pun sumbunya. */
export function axisSpan(top: number, base: number): number {
  return Math.abs(base - top);
}
